/* eslint-env browser */
// client/src/hooks/useEvaluationNudge.js
import React from 'react';
import { useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

// Build base URL safely
const API_BASE_URL =
  (process.env.REACT_APP_API_BASE_URL && process.env.REACT_APP_API_BASE_URL.trim()) ||
  `${(window && window.location && window.location.protocol) || 'http:'}//${(window && window.location && window.location.hostname) || 'localhost'}:5000`;

/** Show the toast UI */
function showToast({ withinWindow, start, end, navigate, reason = '' }) {
  const startText = start
    ? new Date(start).toLocaleString('th-TH', { timeZone: 'Asia/Bangkok' })
    : '-';
  const endText = end
    ? new Date(end).toLocaleString('th-TH', { timeZone: 'Asia/Bangkok' })
    : '-';

  const windowLine = withinWindow
    ? `ขณะนี้เปิดให้ส่งถึง ${endText}`
    : (start || end) ? `ช่วงเวลาการส่ง: ${startText} - ${endText}` : '';

  if (!toast.isActive('eval-nudge-main')) {
    toast.info(
      <div onClick={() => navigate('/dashboard/Evaluations')}>
        <div style={{ fontWeight: 700 }}>กรุณาทำแบบประเมินรายปี</div>
        <div>เพื่อให้การประเมินประจำปีสมบูรณ์</div>
        <div style={{ marginTop: 8, textDecoration: 'underline' }}>คลิกเพื่อไปที่แบบประเมิน</div>
      </div>,
      {
        position: 'top-center',
        autoClose: 8000,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: false,
        theme: 'colored',
        toastId: 'eval-nudge-main',
      }
    );
  }

  if (windowLine && !toast.isActive('eval-nudge-window')) {
    setTimeout(() => {
      toast(windowLine, {
        position: 'top-center',
        autoClose: 6000,
        theme: 'colored',
        toastId: 'eval-nudge-window',
      });
    }, 60);
  }

  // eslint-disable-next-line no-console
  console.log('[EvalNudge] showToast:', { reason, withinWindow, startText, endText });
}

/**
 * useEvaluationNudge
 * - By default, shows for **all logged-in users** if they haven't submitted yet.
 * - Query flags:
 *    - ?forceNudge=1    → show regardless of status (still tries to fetch window)
 *    - ?resetNudge=1    → clear once-per-session flag
 *    - ?disableNudge=1  → skip showing (useful for debugging)
 */
export default function useEvaluationNudge(navigate, { oncePerSession = true } = {}) {
  useEffect(() => {
    const FLAG_KEY = 'eval_prompt_shown';

    (async () => {
      try {
        // eslint-disable-next-line no-console
        console.log('[EvalNudge] API_BASE_URL =', API_BASE_URL);

        const params = new URLSearchParams((window && window.location && window.location.search) || '');
        const forceNudge  = params.get('forceNudge') === '1';
        const resetNudge  = params.get('resetNudge') === '1';
        const disableNudge = params.get('disableNudge') === '1';

        if (resetNudge) {
          window.sessionStorage?.removeItem(FLAG_KEY);
          // eslint-disable-next-line no-console
          console.log('[EvalNudge] resetNudge=1 → cleared session flag');
        }

        if (disableNudge && !forceNudge) {
          // eslint-disable-next-line no-console
          console.log('[EvalNudge] disableNudge=1 → skip');
          return;
        }

        // Identify current user
        const meRes = await axios.get(`${API_BASE_URL}/api/users/get_user_details`, {
          withCredentials: true,
        });
        const me = meRes?.data || {};
        const userId = me?._id;

        // eslint-disable-next-line no-console
        console.log('[EvalNudge] me =', me);

        if (!userId) {
          // eslint-disable-next-line no-console
          console.warn('[EvalNudge] no userId → abort (not logged in / missing cookie)');
          return;
        }

        // Once-per-session gate (skip if already shown unless forcing)
        // if (!forceNudge && oncePerSession && window.sessionStorage?.getItem(FLAG_KEY) === '1') {
        //   // eslint-disable-next-line no-console
        //   console.log('[EvalNudge] already shown this session → abort');
        //   return;
        // }

        // If forceNudge, show regardless of submission status (but try to fetch window for nicer text)
        if (forceNudge) {
          // eslint-disable-next-line no-console
          console.log('[EvalNudge] forceNudge=1 → bypass submission gate');
          let start, end, withinWindow = false;
          try {
            const evalRes = await axios.get(`${API_BASE_URL}/api/evaluations/me`, { withCredentials: true });
            const win = evalRes?.data?.window;
            if (win) {
              start = win.startISO ? new Date(win.startISO).getTime() : 0;
              end   = win.endISO   ? new Date(win.endISO).getTime()   : 0;
              const now = Date.now();
              withinWindow = Boolean(start && end && now >= start && now <= end);
            }
          } catch {
            // eslint-disable-next-line no-console
            console.warn('[EvalNudge] forceNudge: could not load /api/evaluations/me → generic popup');
          }
          showToast({ withinWindow, start, end, navigate, reason: 'forceNudge' });
          if (oncePerSession) window.sessionStorage?.setItem(FLAG_KEY, '1');
          window.__EVAL_DEBUG__ = () =>
            showToast({ withinWindow, start, end, navigate, reason: 'manual __EVAL_DEBUG__' });
          return;
        }

        // Normal path: fetch evaluation status
        const evalRes = await axios.get(`${API_BASE_URL}/api/evaluations/me`, {
          withCredentials: true,
        });
        // eslint-disable-next-line no-console
        console.log('[EvalNudge] GET /api/evaluations/me =', evalRes.data);

        const { submitted, window: evalWindow } = evalRes?.data || {};

        // If already submitted, don't show
        if (submitted) {
          // eslint-disable-next-line no-console
          console.log('[EvalNudge] submitted=true → no popup');
          return;
        }

        const start = evalWindow?.startISO ? new Date(evalWindow.startISO).getTime() : 0;
        const end   = evalWindow?.endISO ? new Date(evalWindow.endISO).getTime() : 0;
        const now   = Date.now();
        const withinWindow = Boolean(start && end && now >= start && now <= end);

        // Show popup
        showToast({ withinWindow, start, end, navigate, reason: 'normal' });

        // Mark once-per-session
        if (oncePerSession) window.sessionStorage?.setItem(FLAG_KEY, '1');

        // Dev helper from console
        window.__EVAL_DEBUG__ = () =>
          showToast({ withinWindow, start, end, navigate, reason: 'manual __EVAL_DEBUG__' });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[EvalNudge] failed:', err?.response?.data || err?.message);
      }
    })();
  }, [navigate, oncePerSession]);
}
